import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import { type ExpenseItemFormSchema } from '@webapp/modules/expense/components/ExpenseLineItemDrawer'
import { ExpensePaymentTypes } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { type ExpenseStatusEnum } from '@webapp/modules/expense/entities/enums/ExpenseStatusEnum'

export interface ExpenseRequestPayload {
  title: string
  department: number
  branch: number
  localCurrency: number
  travel: Nullable<number>
  notes: string
  next_approver: number
  status: number
  items: Record<
    string,
    {
      merchant: string
      account: number
      localCurrency: number
      currency_rate_final: number
      reimburse: boolean
      expense_type: number
      paymentType: number
      creditcard: Nullable<number>
      amount: string
      tax_rate: string
      tax_value: string
      tax_name: string
      tax_percentage: string | undefined
      tax_amount: string | undefined
      transDate: string
      attachment: string
      notes: string
    }
  >
  files: string[]
}

const mapItemToPayload = (
  item: ExpenseItemFormSchema
): ExpenseRequestPayload['items'][string] => {
  let taxRate: string
  let taxValue: string
  let taxName: string
  let taxPercentage: string | undefined
  let taxAmount: string | undefined

  if (item.taxMode === 'percent') {
    taxRate = '%'
    taxName = '%'
    taxValue = item.taxValue || '0'
    taxPercentage = (parseFloat(item.taxValue || '0') / 100).toFixed(4)
    taxAmount = undefined
  } else if (item.taxMode === 'dollar') {
    taxRate = '$'
    taxName = '$'
    taxValue = item.taxValue || '0'
    taxPercentage = undefined
    taxAmount = item.taxValue || '0'
  } else {
    taxRate = String(item.taxId ?? '')
    taxName = String(item.taxId ?? '')
    taxValue = item.taxValue || '0'
    taxPercentage = undefined
    taxAmount = undefined
  }

  let paymentType: number
  let creditcard: Nullable<number>

  if (item.reimbursable) {
    paymentType = ExpensePaymentTypes.OTHER
    creditcard = null
  } else {
    paymentType = item.paymentType
    creditcard = item.paymentMethod?.id ?? null
  }

  return {
    merchant: item.merchant,
    account: item.account?.id ?? 0,
    localCurrency: item.currency.id,
    currency_rate_final: item.currency.rate,
    reimburse: item.reimbursable,
    expense_type: item.expenseType,
    paymentType,
    creditcard,
    amount: String(parseFloat(item.amount || '0')),
    tax_rate: taxRate,
    tax_value: taxValue,
    tax_name: taxName,
    tax_percentage: taxPercentage,
    tax_amount: taxAmount,
    transDate: item.transactionDate,
    attachment: item.attachment,
    notes: item.notes,
  }
}

export const mapExpenseFormFieldsToCreatePayload = (
  formValues: ExpenseReportFormSchema,
  status: ExpenseStatusEnum
): ExpenseRequestPayload => {
  const itemsRecord = formValues.items.reduce<ExpenseRequestPayload['items']>(
    (acc, item, index) => {
      acc[String(index)] = mapItemToPayload(item)
      return acc
    },
    {}
  )

  return {
    title: formValues.title,
    department: formValues.department?.id ?? 0,
    branch: formValues.location?.id ?? 0,
    localCurrency: formValues.items[0]?.currency?.id ?? 0,
    travel: formValues.travel?.id ?? null,
    notes: formValues.notes,
    next_approver: formValues.nextApproverId ?? 0,
    status,
    items: itemsRecord,
    files: [],
  }
}
